<template>
  <div class="passenger-info">
    <!-- 顶部导航栏 -->
    <div class="nav-header">
      <van-nav-bar 
        title="填写乘客信息" 
        left-arrow 
        @click-left="goBack"
        :border="false"
      >
        <template #title>
          <div class="nav-title">
            <i class="title-icon">✍️</i>
            <span>填写乘客信息</span>
          </div>
        </template>
      </van-nav-bar>
    </div>

    <!-- 选中的产品信息卡片 -->
    <div class="selected-product">
      <div class="product-card">
        <div class="product-image">
          <img :src="getHomeImage(selectedVipHall)" alt="" @error="handleImageError">
          <div class="price-tag">
            <span class="vip-price">¥{{ selectedVipHall.priceData[0]?.costPrice || 168 }}</span>
            <span class="original-price" v-if="selectedVipHall.priceData[0]?.marketPrice !== selectedVipHall.priceData[0]?.costPrice">
              ¥{{ selectedVipHall.priceData[0]?.marketPrice || 150 }}
            </span>
          </div>
        </div>
        
        <div class="product-details">
          <h2>{{ selectedVipHall.productName }}</h2>
          
          <div class="booking-info">
            <div class="info-item">
              <i class="icon">🏢</i>
              <span>{{ airportName }}</span>
            </div>
            <div class="info-item">
              <i class="icon">📅</i>
              <span>{{ orderForm.memberServiceOrder.usageTime }}</span>
            </div>
            <div class="info-item">
              <i class="icon">🕐</i>
              <span>{{ selectedVipHall.serverStime }} - {{ selectedVipHall.serverEtime }}</span>
            </div>
          </div>
          
          <div class="location-info">
            <i class="location-icon">📍</i>
            <span>{{ selectedVipHall.addressDetail || '详情请咨询客服' }}</span>
          </div>
          
          <div class="features-preview">
            <span 
              class="feature-tag" 
              v-for="feature in getServiceFeatures(selectedVipHall).slice(0, 4)" 
              :key="feature.id"
            >
              <img :src="feature.url" :alt="feature.value" class="feature-icon" @error="handleIconError">
              {{ feature.value }}
            </span>
            <span class="more-features" v-if="getServiceFeatures(selectedVipHall).length > 4">
              +{{ getServiceFeatures(selectedVipHall).length - 4 }}项服务
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 乘客信息表单 -->
    <div class="form-content">
      <!-- 联系人信息 -->
      <div class="contact-section">
        <div class="section-header">
          <h3>总联系人</h3>
          <p>用于接收订单信息和验证码</p>
        </div>
        
        <div class="form-card">
          <van-field
            v-model="orderForm.memberServiceOrder.linkmanTel"
            label="手机号"
            type="digit"
            maxlength="11"
            placeholder="请输入联系人手机号"
            :rules="[{ pattern: phoneReg, message: '请输入正确的手机号' }]"
            @blur="testPhone(orderForm.memberServiceOrder.linkmanTel)"
          />
        </div>
      </div>

      <!-- 乘客列表 -->
      <div class="passengers-section">
        <div class="section-header">
          <h3>乘客信息 ({{ orderForm.consumerList.length }}人)</h3>
          <div class="add-passenger" @click="handleAddPassenger">
            <van-icon name="plus" />
            <span>添加乘客</span>
          </div>
        </div>

        <div class="passenger-cards">
          <div 
            class="passenger-card" 
            v-for="(passenger, index) in orderForm.consumerList" 
            :key="index"
            :style="{ animationDelay: index * 0.1 + 's' }"
          >
            <div class="card-header">
              <div class="passenger-number">
                <i class="passenger-icon">👤</i>
                <span>乘客 {{ index + 1 }}</span>
              </div>
              <div class="delete-btn" @click="deletePassenger(index)" v-if="orderForm.consumerList.length > 1">
                <van-icon name="delete" />
              </div>
            </div>
            
            <div class="passenger-fields">
              <div class="field-group">
                <van-field
                  v-model="passenger.name"
                  label="姓名"
                  placeholder="请输入真实姓名"
                  @blur="checkFlag(passenger)"
                  :error="!passenger.nameFlag && passenger.name"
                />
              </div>
              
              <div class="field-group">
                <van-field
                  v-model="passenger.tel"
                  label="手机号"
                  type="digit"
                  maxlength="11"
                  placeholder="请输入手机号"
                  @blur="checkFlag(passenger)"
                  :error="!passenger.telFlag && passenger.tel"
                />
              </div>
              
              <div class="field-group">
                <van-field
                  v-model="passenger.idCardNum"
                  label="身份证号"
                  placeholder="请输入身份证号码"
                  @blur="checkFlag(passenger)"
                  :error="!passenger.idCardNumFlag && passenger.idCardNum"
                />
              </div>
              
              <!-- 兑换码输入 -->
              <div class="redeem-section">
                <div class="field-group">
                  <van-field
                    v-model="passenger.pwdCode"
                    label="兑换码"
                    placeholder="请输入兑换码（可选）"
                    @blur="checkRedeemCode(passenger.pwdCode, passenger)"
                  >
                    <template #right-icon>
                      <i class="help-icon" @click="showRedeemHelp">❓</i>
                    </template>
                  </van-field>
                </div>
                
                <div class="code-status" v-if="passenger.pwdCodeErr">
                  <i :class="passenger.pwdCodeFlag ? 'success-icon' : 'error-icon'">
                    {{ passenger.pwdCodeFlag ? '✅' : '❌' }}
                  </i>
                  <span :class="passenger.pwdCodeFlag ? 'success-text' : 'error-text'">
                    {{ passenger.pwdCodeErr }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 价格汇总和提交 -->
      <div class="summary-section">
        <div class="price-breakdown">
          <div class="breakdown-header">
            <h3>费用明细</h3>
          </div>
          
          <div class="price-items">
            <div class="price-item" v-for="(passenger, index) in orderForm.consumerList" :key="index">
              <span class="passenger-name">乘客{{ index + 1 }}</span>
              <span class="price">
                <span v-if="passenger.pwdCodeFlag" class="free-tag">兑换码抵扣</span>
                <span v-else>¥{{ selectedVipHall.priceData[0]?.costPrice || 0 }}</span>
              </span>
            </div>
          </div>
          
          <div class="total-price">
            <span class="total-label">总计</span>
            <span class="total-amount">¥{{ calculateTotalPrice() }}</span>
          </div>
        </div>

        <div class="submit-btn" @click="submitReservation" :class="{ disabled: !canSubmit }">
          <i class="submit-icon">🚀</i>
          <span>立即预约</span>
        </div>
      </div>
    </div>

    <!-- 兑换码帮助弹窗 -->
    <van-popup v-model="showHelp" round position="center" :style="{ width: '80%' }">
      <div class="help-content">
        <h3>兑换码说明</h3>
        <p>• 兑换码是用于抵扣费用的优惠码</p>
        <p>• 一个兑换码只能使用一次</p>
        <p>• 兑换码有效期请咨询客服</p>
        <p>• 如无兑换码可直接提交</p>
        <div class="help-actions">
          <button @click="showHelp = false">我知道了</button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
export default {
  name: 'PassengerInfo',
  data() {
    return {
      selectedVipHall: null,
      airportName: '',
      orderForm: {
        memberServiceOrder: {
          memberServiceId: '',
          usageTime: '',
          memberNo: '',
          linkmanTel: '',
          totalAmount: 0,
          realAmount: 0,
          costAmount: 0,
          orderAmount: 0
        },
        consumerList: [{
          name: '',
          nameFlag: false,
          tel: '',
          telFlag: false,
          idCardNum: '',
          idCardType: 1,
          idCardNumFlag: false,
          pwdCode: '',
          pwdCodeFlag: false,
          pwdCodeErr: ''
        }]
      },
      phoneReg: /^[1][3,4,5,7,8,9][0-9]{9}$/,
      idCardReg: /^[1-9]\d{5}[1-9]\d{3}((0\d)|(1[0-2]))(([0|1|2]\d)|3[0-1])\d{3}([0-9]|X)$/,
      phoneFlag: false,
      showHelp: false
    }
  },
  
  computed: {
    canSubmit() {
      const hasValidContact = this.phoneReg.test(this.orderForm.memberServiceOrder.linkmanTel);
      const hasValidPassengers = this.orderForm.consumerList.every(p => 
        p.nameFlag && p.telFlag && p.idCardNumFlag
      );
      return hasValidContact && hasValidPassengers;
    }
  },
  
  created() {
    this.initPageData();
  },
  
  methods: {
    // 初始化页面数据
    initPageData() {
      const query = this.$route.query;
      
      if (query.vipHall) {
        this.selectedVipHall = JSON.parse(query.vipHall);
      }
      
      if (query.airport) {
        this.airportName = query.airport;
      }
      
      if (query.orderForm) {
        this.orderForm = JSON.parse(query.orderForm);
      }
      
      // 初始化乘客数量
      const num = Number(query.num) || 1;
      if (num > 1) {
        for (let i = 0; i < num - 1; i++) {
          this.handleAddPassenger();
        }
      }
      
      // 预填兑换码
      if (query.pwdCode) {
        const codes = JSON.parse(query.pwdCode);
        codes.forEach((code, index) => {
          if (this.orderForm.consumerList[index]) {
            this.orderForm.consumerList[index].pwdCode = code;
          }
        });
      }
    },
    
    // 返回上一页
    goBack() {
      this.$router.back();
    },
    
    // 获取贵宾厅主图片
    getHomeImage(item) {
      if (!item || !item.imgData || item.imgData.length === 0) {
        return require('@/assets/exchangebg.png');
      }
      
      const homeImg = item.imgData.find(img => img.homeTag === 1);
      if (homeImg) return homeImg.imgUrl;
      
      return item.imgData[0].imgUrl;
    },
    
    // 获取服务设施列表
    getServiceFeatures(item) {
      if (!item || !item.serverContent) return [];
      
      try {
        const serverContent = JSON.parse(item.serverContent);
        const features = [];
        
        if (serverContent.defaultList) {
          features.push(...serverContent.defaultList);
        }
        if (serverContent.defaultfreeList) {
          features.push(...serverContent.defaultfreeList);
        }
        if (serverContent.defaultchargeList) {
          features.push(...serverContent.defaultchargeList);
        }
        
        return features;
      } catch (error) {
        console.error('解析服务内容失败:', error);
        return [];
      }
    },
    
    // 处理图片加载错误
    handleImageError(event) {
      event.target.src = require('@/assets/exchangebg.png');
    },
    
    // 处理服务图标加载错误
    handleIconError(event) {
      event.target.style.display = 'none';
    },
    
    // 验证手机号
    testPhone(num) {
      this.phoneFlag = this.phoneReg.test(num);
      if (!this.phoneFlag && num) {
        this.$toast('请输入正确的手机号');
      }
    },
    
    // 添加乘客
    handleAddPassenger() {
      this.orderForm.consumerList.push({
        name: '',
        nameFlag: false,
        tel: '',
        telFlag: false,
        idCardNum: '',
        idCardType: 1,
        idCardNumFlag: false,
        pwdCode: '',
        pwdCodeFlag: false,
        pwdCodeErr: ''
      });
    },
    
    // 删除乘客
    deletePassenger(index) {
      this.orderForm.consumerList.splice(index, 1);
    },
    
    // 验证字段
    checkFlag(passenger) {
      passenger.nameFlag = !!passenger.name;
      passenger.telFlag = this.phoneReg.test(passenger.tel);
      passenger.idCardNumFlag = this.idCardReg.test(passenger.idCardNum);
    },
    
    // 验证兑换码
    checkRedeemCode(code, passenger) {
      if (!code) {
        passenger.pwdCodeFlag = false;
        passenger.pwdCodeErr = '';
        return;
      }
      
      this.request({
        url: '/product/rcMemberProduct/useProduct/byCode',
        method: 'get',
        params: {
          code: code,
          productId: this.selectedVipHall.id,
          useTime: this.orderForm.memberServiceOrder.usageTime
        }
      }).then(resp => {
        if (resp.code === 200) {
          passenger.pwdCodeFlag = true;
          passenger.pwdCodeErr = '兑换码可用';
          passenger.totalAmount = 0;
        } else {
          passenger.pwdCodeFlag = false;
          passenger.pwdCodeErr = resp.msg;
        }
      }).catch(error => {
        passenger.pwdCodeFlag = false;
        passenger.pwdCodeErr = '验证失败，请重试';
      });
    },
    
    // 计算总价
    calculateTotalPrice() {
      if (!this.selectedVipHall) return 0;
      
      let total = 0;
      const price = this.selectedVipHall.priceData[0]?.costPrice || 0;
      
      this.orderForm.consumerList.forEach(passenger => {
        if (passenger.pwdCodeFlag) {
          total += 0; // 兑换码抵扣
        } else {
          total += price;
        }
      });
      
      return total;
    },
    
    // 显示兑换码帮助
    showRedeemHelp() {
      this.showHelp = true;
    },
    
    // 提交预约
    submitReservation() {
      if (!this.canSubmit) {
        this.$toast('请填写完整信息');
        return;
      }
      
      // 计算价格
      const price = this.selectedVipHall.priceData[0]?.costPrice || 0;
      const marketPrice = this.selectedVipHall.priceData[0]?.marketPrice || 0;
      
      this.orderForm.memberServiceOrder.totalAmount = this.calculateTotalPrice();
      this.orderForm.memberServiceOrder.realAmount = this.calculateTotalPrice();
      this.orderForm.memberServiceOrder.costAmount = this.orderForm.consumerList.length * marketPrice;
      this.orderForm.memberServiceOrder.orderAmount = this.calculateTotalPrice();
      
      // 设置每个乘客的费用
      this.orderForm.consumerList.forEach(passenger => {
        passenger.costAmount = price;
        passenger.totalAmount = passenger.pwdCodeFlag ? 0 : price;
      });
      
      // 跳转到确认订单页面
      this.$router.push({
        path: '/confirm-order-new',
        query: {
          user: JSON.stringify(this.orderForm),
          airport: this.airportName,
          room: this.selectedVipHall.productName
        }
      });
    }
  }
}
</script>

<style lang="less" scoped>
.passenger-info {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  
  .nav-header {
    position: sticky;
    top: 0;
    z-index: 100;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    
    .van-nav-bar {
      background: transparent;
      
      .nav-title {
        display: flex;
        align-items: center;
        gap: 8px;
        
        .title-icon {
          font-size: 20px;
        }
      }
    }
  }
  
  .selected-product {
    padding: 20px 15px 0;
    
    .product-card {
      background: rgba(255, 255, 255, 0.95);
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
      backdrop-filter: blur(10px);
      animation: slideInDown 0.6s ease-out;
      
      .product-image {
        position: relative;
        height: 140px;
        background: linear-gradient(45deg, #f093fb 0%, #f5576c 100%);
        
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .price-tag {
          position: absolute;
          top: 12px;
          right: 12px;
          background: rgba(0, 0, 0, 0.8);
          color: white;
          padding: 8px 12px;
          border-radius: 20px;
          display: flex;
          align-items: center;
          gap: 8px;
          
          .vip-price {
            font-weight: 600;
            font-size: 16px;
          }
          
          .original-price {
            text-decoration: line-through;
            opacity: 0.7;
            font-size: 12px;
          }
        }
      }
      
      .product-details {
        padding: 20px;
        
        h2 {
          font-size: 20px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 16px;
        }
        
        .booking-info {
          display: flex;
          gap: 16px;
          margin-bottom: 12px;
          flex-wrap: wrap;
          
          .info-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 14px;
            color: #7f8c8d;
            
            .icon {
              font-size: 16px;
            }
          }
        }
        
        .location-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          
          .location-icon {
            font-size: 14px;
          }
          
          span {
            font-size: 14px;
            color: #7f8c8d;
          }
        }
        
        .features-preview {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          
          .feature-tag {
            background: rgba(25, 137, 250, 0.1);
            color: #1989fa;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            display: flex;
            align-items: center;
            gap: 4px;
            
            .feature-icon {
              width: 12px;
              height: 12px;
              object-fit: contain;
            }
          }
          
          .more-features {
            background: rgba(52, 152, 219, 0.1);
            color: #3498db;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
          }
        }
      }
    }
  }
  
  .form-content {
    padding: 20px 15px;
    
    .section-header {
      margin-bottom: 16px;
      
      h3 {
        font-size: 18px;
        font-weight: 600;
        color: white;
        margin-bottom: 4px;
      }
      
      p {
        font-size: 14px;
        color: rgba(255, 255, 255, 0.8);
      }
    }
    
    .contact-section {
      margin-bottom: 24px;
      
      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
      }
      
      .form-card {
        background: rgba(255, 255, 255, 0.95);
        border-radius: 16px;
        padding: 20px;
        backdrop-filter: blur(10px);
        animation: slideInUp 0.6s ease-out;
      }
    }
    
    .passengers-section {
      margin-bottom: 24px;
      
      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        margin-bottom: 16px;
        
        .add-passenger {
          display: flex;
          align-items: center;
          gap: 4px;
          color: rgba(255, 255, 255, 0.9);
          cursor: pointer;
          font-size: 14px;
          background: rgba(255, 255, 255, 0.2);
          padding: 8px 12px;
          border-radius: 20px;
          transition: all 0.3s ease;
          
          &:hover {
            background: rgba(255, 255, 255, 0.3);
          }
        }
      }
      
      .passenger-cards {
        .passenger-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 16px;
          padding: 20px;
          margin-bottom: 16px;
          backdrop-filter: blur(10px);
          animation: slideInRight 0.5s ease-out both;
          
          .card-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            
            .passenger-number {
              display: flex;
              align-items: center;
              gap: 8px;
              
              .passenger-icon {
                font-size: 18px;
              }
              
              span {
                font-size: 16px;
                font-weight: 600;
                color: #2c3e50;
              }
            }
            
            .delete-btn {
              color: #e74c3c;
              cursor: pointer;
              padding: 8px;
              border-radius: 50%;
              transition: background 0.3s ease;
              
              &:hover {
                background: rgba(231, 76, 60, 0.1);
              }
            }
          }
          
          .passenger-fields {
            .field-group {
              margin-bottom: 16px;
            }
            
            .redeem-section {
              border-top: 1px solid #ecf0f1;
              padding-top: 16px;
              
              .help-icon {
                color: #3498db;
                cursor: pointer;
                font-size: 14px;
              }
              
              .code-status {
                display: flex;
                align-items: center;
                gap: 8px;
                margin-top: 8px;
                padding: 8px 12px;
                border-radius: 8px;
                background: #f8f9fa;
                
                .success-text {
                  color: #27ae60;
                }
                
                .error-text {
                  color: #e74c3c;
                }
              }
            }
          }
        }
      }
    }
    
    .summary-section {
      .price-breakdown {
        background: rgba(255, 255, 255, 0.95);
        border-radius: 16px;
        padding: 20px;
        margin-bottom: 20px;
        backdrop-filter: blur(10px);
        animation: slideInUp 0.6s ease-out;
        
        .breakdown-header {
          margin-bottom: 16px;
          
          h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;
          }
        }
        
        .price-items {
          .price-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 0;
            border-bottom: 1px solid #ecf0f1;
            
            &:last-child {
              border-bottom: none;
            }
            
            .passenger-name {
              color: #7f8c8d;
            }
            
            .free-tag {
              background: #27ae60;
              color: white;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 12px;
            }
          }
        }
        
        .total-price {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
          border-top: 2px solid #ecf0f1;
          margin-top: 16px;
          
          .total-label {
            font-size: 18px;
            font-weight: 600;
            color: #2c3e50;
          }
          
          .total-amount {
            font-size: 24px;
            font-weight: 600;
            color: #e74c3c;
          }
        }
      }
      
      .submit-btn {
        background: linear-gradient(135deg, #00b894 0%, #00a085 100%);
        color: white;
        padding: 16px;
        border-radius: 16px;
        text-align: center;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 16px;
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 184, 148, 0.3);
        }
        
        &.disabled {
          background: #bdc3c7;
          cursor: not-allowed;
          
          &:hover {
            transform: none;
            box-shadow: none;
          }
        }
        
        .submit-icon {
          font-size: 18px;
        }
      }
    }
  }
  
  .help-content {
    padding: 24px;
    text-align: center;
    
    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #2c3e50;
      margin-bottom: 16px;
    }
    
    p {
      text-align: left;
      margin-bottom: 8px;
      color: #7f8c8d;
    }
    
    .help-actions {
      margin-top: 20px;
      
      button {
        background: #1989fa;
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 8px;
        cursor: pointer;
      }
    }
  }
}

// 动画
@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}
</style> 